export type ChartType =
  | "bar"
  | "line"
  | "area"
  | "pie"
  | "donut"
  | "radar"
  | "scatter"
  | "heatmap"
  | "treemap"
  | "funnel"
  | "radialBar"
  | "scorecard"
  | "table"
  | "text"
  | "image";

export interface AxisConfig {
  showXAxis: boolean;
  showYAxis: boolean;
  showGridlines: boolean;
  xAxisTitle: string;
  yAxisTitle: string;
}

export interface ChartSpecifics {
  orientation?: "vertical" | "horizontal";
  stacked?: boolean;
  smooth?: boolean;
  strokeWidth?: number;
  innerRadius?: number;
  showDataLabels?: boolean;
  showLegend?: boolean;
  legendPosition?: "top" | "bottom" | "left" | "right";
  barBorderRadius?: number;
  fillOpacity?: number;
  textContent?: string;
  imageUrl?: string;
  scorecardValue?: string;
  scorecardLabel?: string;
  scorecardPrefix?: string;
  scorecardSuffix?: string;
  tableHeaders?: string[];
  tableRows?: string[][];
}

export interface UIConfig {
  global: {
    backgroundColor: string;
    padding: number;
    borderRadius: number;
    showBorder?: boolean;
    borderColor?: string;
    borderWidth?: number;
    resizeAllSides?: boolean;
  };
  axes: AxisConfig;
  specifics: ChartSpecifics;
}

export interface WidgetData {
  labels: string[];
  series: { name: string; data: number[] }[];
}

export interface Widget {
  id: string;
  type: ChartType;
  title: string;
  layout: { x: number; y: number; w: number; h: number };
  data: WidgetData;
  uiConfig: UIConfig;
  colors: string[];
}

export interface AnalyticsTheme {
  mode: "light" | "dark";
  canvasPadding: number;
  defaultColors: string[];
  canvasBackgroundColor?: string;
  canvasWidth?: string;
  showCanvasGridPattern?: boolean;
}

export interface Dashboard {
  id: string;
  name: string;
  widgets: Widget[];
  global: AnalyticsTheme;
  isTrashed?: boolean;
  createdAt?: number;
}

export interface AppState {
  dashboards: Dashboard[];
  activeDashboardId: string | null;
  activeWidgetId: string | null;
}

const DEFAULT_COLORS = ["#2563EB","#10B981","#F59E0B","#EF4444","#8B5CF6","#EC4899","#06B6D4","#84CC16","#F97316","#6366F1"];

function defaultUIConfig(type: ChartType): UIConfig {
  const base: UIConfig = {
    global: { backgroundColor: "#FFFFFF", padding: 16, borderRadius: 8, showBorder: false, borderColor: "#DADCE0", borderWidth: 1, resizeAllSides: true },
    axes: { showXAxis: true, showYAxis: true, showGridlines: true, xAxisTitle: "", yAxisTitle: "" },
    specifics: { showDataLabels: false, showLegend: true, legendPosition: "bottom" },
  };
  switch (type) {
    case "bar": base.specifics.orientation = "vertical"; base.specifics.stacked = false; base.specifics.barBorderRadius = 4; break;
    case "line": base.specifics.smooth = false; base.specifics.strokeWidth = 3; break;
    case "area": base.specifics.smooth = true; base.specifics.strokeWidth = 2; base.specifics.fillOpacity = 0.35; break;
    case "pie": case "donut":
      base.axes.showXAxis = false; base.axes.showYAxis = false; base.axes.showGridlines = false;
      if (type === "donut") base.specifics.innerRadius = 55; break;
    case "radar": base.axes.showGridlines = true; base.specifics.fillOpacity = 0.3; break;
    case "scatter": base.specifics.strokeWidth = 0; break;
    case "heatmap": base.axes.showGridlines = false; break;
    case "treemap": case "funnel": case "radialBar":
      base.axes.showXAxis = false; base.axes.showYAxis = false; base.axes.showGridlines = false; break;
    case "scorecard":
      base.axes.showXAxis = false; base.axes.showYAxis = false; base.axes.showGridlines = false;
      base.specifics.scorecardValue = "1,234"; base.specifics.scorecardLabel = "Total";
      base.specifics.scorecardPrefix = ""; base.specifics.scorecardSuffix = ""; break;
    case "table":
      base.axes.showXAxis = false; base.axes.showYAxis = false; base.axes.showGridlines = false;
      base.specifics.tableHeaders = ["Name", "Value", "Change"];
      base.specifics.tableRows = [["Revenue","$45,200","+12%"],["Users","3,450","+8%"],["Conversions","234","+15%"],["Bounce Rate","42%","-3%"]]; break;
    case "text":
      base.axes.showXAxis = false; base.axes.showYAxis = false; base.axes.showGridlines = false;
      base.specifics.textContent = "Double-click to edit this text block."; break;
    case "image":
      base.axes.showXAxis = false; base.axes.showYAxis = false; base.axes.showGridlines = false;
      base.specifics.imageUrl = ""; break;
  }
  return base;
}

const SAMPLE_DATA: Record<ChartType, WidgetData> = {
  bar: { labels: ["Jan","Feb","Mar","Apr","May","Jun"], series: [{ name: "Revenue", data: [44,55,57,56,61,58] },{ name: "Expenses", data: [35,41,36,26,45,48] }] },
  line: { labels: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"], series: [{ name: "Sessions", data: [31,40,28,51,42,109,100] },{ name: "Page Views", data: [11,32,45,32,34,52,41] }] },
  area: { labels: ["Q1","Q2","Q3","Q4"], series: [{ name: "2024", data: [28,29,33,36] },{ name: "2025", data: [12,19,27,35] }] },
  pie: { labels: ["Direct","Organic","Referral","Social"], series: [{ name: "Traffic", data: [44,30,15,11] }] },
  donut: { labels: ["Chrome","Safari","Firefox","Edge"], series: [{ name: "Browsers", data: [55,23,12,10] }] },
  radar: { labels: ["Speed","Design","Usability","Features","Support"], series: [{ name: "Product A", data: [80,50,30,40,100] },{ name: "Product B", data: [20,30,40,80,20] }] },
  scatter: { labels: [], series: [{ name: "Sample A", data: [10,20,30,40,50,60,70,80,90,100] },{ name: "Sample B", data: [15,25,18,45,55,38,75,62,88,95] }] },
  heatmap: { labels: ["Mon","Tue","Wed","Thu","Fri"], series: [{ name: "Morning", data: [10,20,30,15,25] },{ name: "Afternoon", data: [25,35,20,30,40] },{ name: "Evening", data: [15,10,25,20,30] }] },
  treemap: { labels: [], series: [{ name: "Revenue", data: [120,90,80,70,60,50] }] },
  funnel: { labels: ["Visitors","Leads","Qualified","Proposals","Won"], series: [{ name: "Funnel", data: [1200,800,500,300,150] }] },
  radialBar: { labels: ["Progress","Target","Actual"], series: [{ name: "Completion", data: [76,67,85] }] },
  scorecard: { labels: [], series: [] },
  table: { labels: [], series: [] },
  text: { labels: [], series: [] },
  image: { labels: [], series: [] },
};

const CHART_TYPE_LABELS: Record<ChartType, string> = {
  bar: "Bar Chart", line: "Line Chart", area: "Area Chart", pie: "Pie Chart",
  donut: "Donut Chart", radar: "Radar Chart", scatter: "Scatter Plot", heatmap: "Heatmap",
  treemap: "Treemap", funnel: "Funnel Chart", radialBar: "Gauge / Radial",
  scorecard: "Scorecard", table: "Table", text: "Text Block", image: "Image",
};

const CHART_TYPE_ICONS: Record<ChartType, string> = {
  bar: "fa-chart-bar", line: "fa-chart-line", area: "fa-chart-area", pie: "fa-chart-pie",
  donut: "fa-circle-notch", radar: "fa-bullseye", scatter: "fa-braille", heatmap: "fa-th",
  treemap: "fa-th-large", funnel: "fa-filter", radialBar: "fa-tachometer-alt",
  scorecard: "fa-digital-tachograph", table: "fa-table", text: "fa-font", image: "fa-image",
};

let _idCounter = 0;
function generateId(): string { _idCounter += 1; return `widget_${Date.now()}_${_idCounter}`; }

export function createWidget(type: ChartType, position?: { x: number; y: number }): Widget {
  const sizes: Record<ChartType, { w: number; h: number }> = {
    bar:{w:6,h:4}, line:{w:6,h:4}, area:{w:6,h:4}, pie:{w:4,h:4}, donut:{w:4,h:4},
    radar:{w:4,h:4}, scatter:{w:6,h:4}, heatmap:{w:6,h:4}, treemap:{w:6,h:4},
    funnel:{w:4,h:5}, radialBar:{w:4,h:4}, scorecard:{w:3,h:2}, table:{w:6,h:4},
    text:{w:4,h:2}, image:{w:4,h:4},
  };
  const s = sizes[type];
  return {
    id: generateId(), type, title: CHART_TYPE_LABELS[type],
    layout: { x: position?.x ?? 0, y: position?.y ?? Infinity, w: s.w, h: s.h },
    data: { ...SAMPLE_DATA[type] }, uiConfig: defaultUIConfig(type), colors: [...DEFAULT_COLORS],
  };
}

export function createDashboard(name: string): Dashboard {
  return {
    id: `dashboard_${Date.now()}`,
    name,
    widgets: [],
    global: { mode: "light", canvasPadding: 24, defaultColors: [...DEFAULT_COLORS], canvasBackgroundColor: "#F1F3F4", canvasWidth: "100%", showCanvasGridPattern: false },
    createdAt: Date.now(),
  };
}

const STORAGE_KEY = "cloudee_custom_analytics_app";
const OLD_STORAGE_KEY = "cloudee_custom_analytics";

export function saveState(s: AppState): void { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {} }

export function loadState(): AppState | null { 
  try { 
    const r = localStorage.getItem(STORAGE_KEY); 
    if (r) return JSON.parse(r); 
    
    // Migration from old single-dashboard state
    const old = localStorage.getItem(OLD_STORAGE_KEY);
    if (old) {
      const oldState = JSON.parse(old);
      if (oldState && oldState.widgets) {
        const migratedDashboard: Dashboard = {
          id: `dashboard_migrated_${Date.now()}`,
          name: "My Dashboard",
          widgets: oldState.widgets,
          global: oldState.global || { mode: "light", canvasPadding: 24, defaultColors: [...DEFAULT_COLORS], canvasBackgroundColor: "#F1F3F4", canvasWidth: "100%", showCanvasGridPattern: false },
          createdAt: Date.now(),
        };
        const migratedApp: AppState = {
          dashboards: [migratedDashboard],
          activeDashboardId: migratedDashboard.id,
          activeWidgetId: null,
        };
        saveState(migratedApp);
        return migratedApp;
      }
    }
    
    return null; 
  } catch { return null; } 
}

export function getInitialState(): AppState {
  const saved = loadState();
  if (saved && saved.dashboards && saved.dashboards.length > 0) return saved;
  const initialDash = createDashboard("My Dashboard");
  return { dashboards: [initialDash], activeDashboardId: initialDash.id, activeWidgetId: null };
}

export { DEFAULT_COLORS, CHART_TYPE_LABELS, CHART_TYPE_ICONS, SAMPLE_DATA };
